import React, { useContext, useState } from 'react';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Space, Tag, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import ServicesContext from '@totem/components/administration/services/ServicesContext';
import { ServiceDefinition } from '@totem/components/administration/services/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';

const ServicesTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { loading, data, onEdit, onDelete } = useContext(ServicesContext);
  const [pageSize, setPageSize] = useState<number>(10);

  const canEdit =
    permissions.internalSystemAdministrator.hasPermissions(userProfile);

  const columns: ColumnProps<ServiceDefinition>[] = [
    {
      title: 'Display Name',
      key: 'displayName',
      sorter: (compA, compB) =>
        compA.displayName.localeCompare(compB.displayName),
      defaultSortOrder: 'ascend',
      render: (_, record) => <span>{record.displayName}</span>,
    },
    {
      title: 'Internal Name',
      key: 'name',
      sorter: (compA, compB) => compA.name.localeCompare(compB.name),
      render: (_, record) => <code>{record.name}</code>,
    },
    {
      title: 'Description',
      key: 'description',
      render: (_, record) => <span>{record.description}</span>,
    },
    {
      title: 'Aliases',
      key: 'aliases',
      render: (_, record) => (
        <span>
          {(record.aliases ?? []).map((alias) => (
            <Tag key={alias}>{alias}</Tag>
          ))}
        </span>
      ),
    },
    {
      title: 'Tool Keys',
      key: 'toolKeys',
      render: (_, record) => (
        <span>
          {(record.commissioning?.toolKeys ?? []).map((key) => (
            <Tag key={key} color="blue">
              {key}
            </Tag>
          ))}
        </span>
      ),
    },
    {
      title: 'API Site Types',
      key: 'toolApiSiteTypes',
      render: (_, record) => (
        <span>
          {(record.commissioning?.toolApiSiteTypes ?? []).map((siteType) => (
            <Tag key={siteType} color="purple">
              {siteType}
            </Tag>
          ))}
        </span>
      ),
    },
    {
      title: 'Req. Device',
      key: 'requiresDevice',
      width: 110,
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) =>
        record.commissioning?.requiresDevice === value,
      render: (_, record) =>
        record.commissioning?.requiresDevice ? (
          <Tag color="green">Yes</Tag>
        ) : (
          <Tag>No</Tag>
        ),
    },
    {
      title: 'Req. Building',
      key: 'requiresBuilding',
      width: 120,
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) =>
        record.commissioning?.requiresBuilding === value,
      render: (_, record) =>
        record.commissioning?.requiresBuilding ? (
          <Tag color="green">Yes</Tag>
        ) : (
          <Tag>No</Tag>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) =>
        canEdit ? (
          <Space>
            <Tooltip title="Edit">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
              />
            </Tooltip>
            <Popconfirm
              title="Delete this service?"
              description="This cannot be undone."
              onConfirm={() => onDelete(record.id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete">
                <Button size="small" icon={<DeleteOutlined />} danger />
              </Tooltip>
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  return (
    <IBTable
      rowKey={(record) => record.id}
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={{
        pageSize,
        showSizeChanger: true,
        onShowSizeChange: (_, size) => setPageSize(size),
      }}
    />
  );
};

export default ServicesTable;
